import React, { useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import type { Migration, TransformationConfig, Resource } from '../../types';
import { SelectedResourcesTableHeader } from '../../components/resource/table/SelectedResourcesTableHeader';
import SelectedResourcesTableBody from '../../components/resource/table/SelectedResourcesTableBody';
import { SetupLayout } from '../../components/layout/SetupLayout';
import { logger } from '../../utils/logger';
import {
  approveExportAndStartTransformation,
  rerunApproveExportWithTransformation,
  rerunApproveExportWithoutTransformation,
  isApproveExportRerunStatus
} from '../../services/Migration/updateMigrationService';
import skipTransformation from '../../services/Migration/skipTransformationService';
import { useToast } from '../../components/Toast/ToastProvider';
import { useAuth } from '../../hooks/auth';
import { useResourceSearch, useSmartBack } from '../../hooks/ui';
import { useFetchExportedResources, useFetchOrganizations, useFetchDivisions } from '../../hooks/api';
import {
  mapApiTypeToDisplayName,
  mapDisplayNamesToTerraformTypes
} from '../../utils/resourceTypeMapper';
import { formatRegion } from '../../utils/formatters';
import type { TransformationDefinition } from '../../types/migration';

import {
  downloadMigrationResults,
  NoResultsAvailableError
} from '../../services/Migration/downloadMigrationResultsService';
import TransformationForm, {
  type TransformationFormData
} from '../../components/ApproveExport/transformations/TransformationForm';
import { RESOURCE_TYPES } from '../../utils/resourceTypes';
import { MigrationProgressStepper } from '../../components/ProgressTracker/MigrationProgressStepper';
import { APPROVE_EXPORT_STEPS } from '../../utils/migrationSteps';
import { ui } from '../../config/uiSizes';
import { ConfirmApproveExportModal } from '../../components/Modals/Migration/ConfirmApproveExportModal';
import { PreMigrationConfirmModal } from '../../components/Modals/Migration/PreMigrationConfirmModal';
import MigrationWarningsBanner from '../../components/Migrations/MigrationWarningsBanner';
import { TRANSFORMATION_TYPES } from '../../types/transformations';


const ApproveExportPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const smartBack = useSmartBack();
  const { accessToken } = useAuth();
  const { showToast } = useToast();
  const selectedJobFromState = location.state?.job as Migration | undefined;
  const isEditMode = location.state?.isEdit === true;
  const savedTransformations = location.state?.transformations as TransformationFormData[] | undefined;

  const selectedJob = selectedJobFromState;
  // Detect D2D reliably from the typed migrationType now serialized by the API.
  // Fall back to checking for the auto-applied DivisionMapping for older records
  // created before migrationType was persisted. The fallback must NOT trigger for
  // non-D2D migrations where the user manually added a DivisionMapping transformation —
  // the auto-applied one is always at order 0 with the "Auto-applied" description.
  const hasAutoAppliedDivisionMapping = selectedJob?.transformationConfig?.transformations?.some(
    (t) =>
      t.name?.toLowerCase() === 'divisionmapping' &&
      (t.order === 0 || t.description?.toLowerCase().includes('auto-applied'))
  ) ?? false;
  const isD2D = (
    selectedJob?.migrationType === 'DivisionToDivision' ||
    hasAutoAppliedDivisionMapping
  );

  // Fetch divisions to resolve GUIDs to friendly names for the D2D info card
  const { divisions: d2dDivisions, loading: d2dDivisionsLoading, fetchDivisions: fetchD2dDivisions } = useFetchDivisions(
    isD2D ? (selectedJob?.sourceOrgName ?? '') : ''
  );
  React.useEffect(() => {
    if (isD2D && selectedJob?.sourceOrgName) {
      void fetchD2dDivisions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isD2D, selectedJob?.sourceOrgName]);
  // For D2D migrations, hide DivisionMapping from the "Add transformation" list — it is auto-applied
  const availableTransformationTypes = React.useMemo(
    () => isD2D ? TRANSFORMATION_TYPES.filter((t) => t.value !== 'DivisionMapping') : TRANSFORMATION_TYPES,
    [isD2D]
  );

  const [transformations, setTransformations] = useState<TransformationFormData[]>(savedTransformations || []);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const hasLoadedTransformations = React.useRef(false);
  const [typeSearch, setTypeSearch] = React.useState('');
  const [nameSearch, setNameSearch] = React.useState('');
  const [isFullSnapshotMode] = React.useState(false);
  const [validationError, setValidationError] = React.useState<string[]>([]);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = React.useState(false);
  const [confirmApproveOpen, setConfirmApproveOpen] = React.useState(false);
  const [preMigrationConfirmOpen, setPreMigrationConfirmOpen] = React.useState(false);

  // Detect if this is a rerun from a failed state (mirrors MigrationSetup / SnapshotSetup pattern)
  const isRerun = React.useMemo(
    () => isApproveExportRerunStatus(selectedJob?.status ?? ''),
    [selectedJob?.status]
  );

  // Fetch the actually exported resources from the backend
  const {
    exportedResources,
    loading: exportedResourcesLoading,
    error: exportedResourcesError
  } = useFetchExportedResources(accessToken, selectedJob?.id);

  // Fetch organizations for region display in confirmation modal
  const { organizations, fetchOrganizations } = useFetchOrganizations();

  // Log page mount for traceability
  React.useEffect(() => {
    logger.info('Page: ApproveExport mounted', { jobId: selectedJob?.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch organizations on mount
  React.useEffect(() => {
    if (accessToken) {
      fetchOrganizations();
    }
  }, [accessToken, fetchOrganizations]);

  // Debug logging for organizations data
  React.useEffect(() => {
    logger.info('🔍 ApproveExportPage - Organizations data:', {
      organizationsCount: organizations.length,
      organizations: organizations,
      targetOrgName: selectedJob?.targetOrgName,
      matchingOrg: organizations.find(o => o.name === selectedJob?.targetOrgName)
    });
  }, [organizations, selectedJob?.targetOrgName]);

  // Transform exported resources from the API response into Resource[] format
  const resources: Resource[] = React.useMemo(() => {
    if (!exportedResources?.resourceTypes) {
      return [];
    }

    const resourceList: Resource[] = [];
    let globalIndex = 0;

    // Iterate through each resource type and its resources
    Object.entries(exportedResources.resourceTypes).forEach(([resourceType, resourceNames]) => {
      if (Array.isArray(resourceNames)) {
        resourceNames.forEach((resourceName: string) => {
          // Convert API type to display name (e.g., 'genesyscloud_architect_user_prompt' -> 'Architect User Prompt')
          const displayType = mapApiTypeToDisplayName(resourceType);

          resourceList.push({
            id: `${resourceType}::${resourceName}::${globalIndex}`,
            type: displayType,
            name: resourceName
          });
          globalIndex++;
        });
      }
    });

    return resourceList;
  }, [exportedResources]);

  //Derive unique resource types for filter dropdown
  const selectedResourceTypes = Array.from(new Set(resources.map((r) => r.type)));

  // Map of resource-type display name -> resource names, for the transformation name-change preview.
  const resourcesByType: Record<string, string[]> = React.useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const resource of resources) {
      (map[resource.type] ??= []).push(resource.name);
    }
    return map;
  }, [resources]);

  const { filteredResourcesByType } = useResourceSearch(resources, typeSearch, nameSearch);

  const filteredResourceTypes = React.useMemo(() => {
    if (!typeSearch.trim()) return RESOURCE_TYPES;
    const searchLower = typeSearch.trim().toLowerCase();
    return RESOURCE_TYPES.filter((type) => type.toLowerCase().includes(searchLower));
  }, [typeSearch]);

  const selectedResourceTitle = 'Exported Resources';

  const totalSelectedCount = resources.length;
  const tableScrollClass = `h-72 overflow-y-auto p-3`;
  const [startTransformation, setStartTransformation] = React.useState(false);

  const isFormValid = React.useMemo(() => {
    if (transformations.length === 0) {
      return true; // No transformations means nothing to validate, so it's valid
    }

    for (const transformation of transformations) {
      if (!transformation.transformationType) {
        return false; // Transformation type is required
      }

      // if transformation type is selected then at least one resource type must be selected
      if (transformation.resourceTypes.length === 0) {
        return false;
      }
    }

    return true;
  }, [transformations]);

  const isButtonEnabled = isFormValid;

  // validate form and show error messages if invalid when user attempts to start transformation
  const validateForm = useCallback(() => {
    const errors: string[] = [];

    transformations.forEach((transformation, index) => {
      if (!transformation.transformationType) {
        errors.push(`Transformation #${index + 1}: Transformation type is required. Or remove this transformation if not needed.`);
      }
      if (transformation.resourceTypes.length === 0) {
        errors.push(`Transformation #${index + 1}: At least one resource type must be selected. Or remove this transformation if not needed.`);
      }
    });

    if (errors.length > 0) {
      setValidationError(errors);
      return false;
    }

    setValidationError([]);
    return true;
  }, [transformations]);

  React.useEffect(() => {
    if (validationError.length > 0 && isFormValid) {
      setValidationError([]);
    }
  }, [validationError, isFormValid]);

  React.useEffect(() => {
    if (hasAttemptedSubmit) {
      validateForm();
    }
  }, [transformations, hasAttemptedSubmit, validateForm]);

  // Populate transformations when in edit mode or rerun mode
  React.useEffect(() => {
    // Skip loading if we already have saved transformations from navigation
    if (savedTransformations && savedTransformations.length > 0 && !hasLoadedTransformations.current) {
      hasLoadedTransformations.current = true;
      // For D2D migrations, strip the auto-applied DivisionMapping from the editable list —
      // it is already shown in the info card above the transformations list.
      const filtered = isD2D
        ? savedTransformations.filter((t) => t.transformationType?.toLowerCase() !== 'divisionmapping')
        : savedTransformations;
      setTransformations(filtered);
      logger.info('Restored transformations from navigation state', {
        count: filtered.length
      });
      return;
    }

    if ((isEditMode || isRerun) && selectedJob?.transformationConfig && resources.length > 0 && !hasLoadedTransformations.current) {
      const configs = Array.isArray(selectedJob.transformationConfig)
        ? selectedJob.transformationConfig
        : [selectedJob.transformationConfig];

      const loadedTransformations: TransformationFormData[] = [];

      configs.forEach((config: TransformationConfig) => {
        config.transformations.forEach((transform: TransformationDefinition) => {
          // For D2D migrations, skip the auto-applied DivisionMapping — it is shown in the info card.
          if (isD2D && transform.name?.toLowerCase() === 'divisionmapping') return;
          const displayResourceTypes = (transform.applyToTypes || []).map((apiType) =>
            mapApiTypeToDisplayName(apiType)
          );

          loadedTransformations.push({
            id: `transformation-${transform.name}-${transform.order || 0}`,
            transformationType: transform.name,
            resourceTypes: displayResourceTypes,
            displayName:
              TRANSFORMATION_TYPES.find((t) => t.value === transform.name)?.label ||
              transform.name,
            isCollapsed: true,
            transformation: {
              enabled: transform.enabled,
              name: transform.name,
              order: transform.order || loadedTransformations.length + 1,
              parameters: transform.parameters || {}
            }
          });
        });
      });

      setTransformations(loadedTransformations);
      hasLoadedTransformations.current = true;
      logger.info(
        isEditMode
          ? 'Loaded transformations in edit mode'
          : 'Loaded transformations in rerun mode',
        {
          count: loadedTransformations.length,
          transformations: loadedTransformations,
          availableResourceTypes: selectedResourceTypes
        }
      );

      // Expand all transformations after a short delay to allow rendering to complete
      setTimeout(() => {
        setTransformations((prev) => prev.map((t) => ({ ...t, isCollapsed: false })));
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, isRerun, selectedJob, resources, selectedResourceTypes, savedTransformations]);



  const handleStartTransformation = async () => {
    if (!selectedJob?.id) {
      logger.error('No migration ID available for export approval');
      return;
    }

    setStartTransformation(true);
    const hasTransformations = transformations.length > 0;

    logger.info(
      isRerun || isEditMode
        ? `${isEditMode ? 'Editing and rerunning' : 'Rerunning'} export approval for "${selectedJob.name}" (status: ${selectedJob.status}, hasTransformations: ${hasTransformations})`
        : `Starting export approval for "${selectedJob.name}" (hasTransformations: ${hasTransformations})`,
      { jobId: selectedJob.id, migrationName: selectedJob.name, isRerun, isEditMode, hasTransformations }
    );

    try {
      // For D2D migrations, prepend the auto-applied DivisionMapping (order 0) so it is
      // always included in the config sent to the backend, even when the user added no
      // additional transformations. User-added transforms remain at order 1, 2, 3…
      const autoAppliedDivisionMapping = isD2D
        ? selectedJob.transformationConfig?.transformations?.find(
          (t) => t.name?.toLowerCase() === 'divisionmapping'
        )
        : undefined;

      // Map user-added transformations to the API format (order starts at 1). Replace Prefix is now
      // a first-class backend transformation (atomic old→new prefix swap that also rewrites flow
      // published names/references consistently), so it is sent directly rather than expanded into a
      // RemovePrefix + AddPrefix pair (that pair produced UAT-TEST-Flow1 on flows).
      const userTransformationsForApi = transformations
        .flatMap((t) => {
          const terraformResourceTypes = mapDisplayNamesToTerraformTypes(t.resourceTypes);
          const description =
            TRANSFORMATION_TYPES.find((type) => type.value === t.transformationType)?.description || '';
          const params = t.transformation.parameters || {};

          return [
            {
              enabled: true,
              name: t.transformationType,
              applyToTypes: terraformResourceTypes,
              description,
              parameters: params
            }
          ];
        })
        .map((entry, index) => ({ ...entry, order: index + 1 }));

      // Prepend DivisionMapping at order 0 for D2D migrations
      const transformationsForApi = autoAppliedDivisionMapping
        ? [
          {
            enabled: autoAppliedDivisionMapping.enabled ?? true,
            name: autoAppliedDivisionMapping.name,
            order: 0,
            applyToTypes: autoAppliedDivisionMapping.applyToTypes ?? [],
            description: autoAppliedDivisionMapping.description ?? 'Auto-applied division remapping for Division-to-Division migration',
            parameters: autoAppliedDivisionMapping.parameters ?? {}
          },
          ...userTransformationsForApi
        ]
        : userTransformationsForApi;

      const transformationConfig: TransformationConfig = {
        transformations: transformationsForApi,
        settings: {
          logLevel: 'Information',
          validateBeforeApply: true,
          createBackups: false,
          stopOnError: false,
          maxParallelOperations: 1
        }
      };

      // D2D migrations always need the transformation pipeline to run (DivisionMapping must
      // execute) even when the user added no additional transformations.
      const needsTransformationPipeline = hasTransformations || isD2D;

      let response;

      if (isRerun || isEditMode) {
        // Rerun/Edit path — reset the migration to a valid pre-transformation state and re-queue it
        // using updateMigrationService (never creates a new migration).
        // This applies both for failed imports (isRerun) and editing existing transformations (isEditMode).
        if (needsTransformationPipeline) {
          logger.info(
            isEditMode
              ? 'Edit mode: resetting to ExportComplete and re-queuing transformation with updated config'
              : 'Rerunning: resetting to ExportComplete and re-queuing transformation',
            {
              jobId: selectedJob.id,
              transformationCount: transformations.length,
              isD2D,
              isEditMode,
              isRerun
            }
          );
          response = await rerunApproveExportWithTransformation(
            accessToken || '',
            selectedJob.id,
            transformationConfig
          );
        } else {
          // No transformations on rerun/edit — reset to ExportComplete and advance to ImportQueued,
          // skipping the transformation pipeline entirely.
          logger.info(
            isEditMode
              ? 'Edit transformation mode: resetting to ExportComplete and skipping transformation (no transformations)'
              : 'Rerunning failed import: resetting to ExportComplete and skipping transformation (no transformations)',
            {
              jobId: selectedJob.id,
              isEditMode,
              isRerun
            }
          );
          response = await rerunApproveExportWithoutTransformation(
            accessToken || '',
            selectedJob.id
          );
        }
      } else {
        // First-time approval path.
        if (needsTransformationPipeline) {
          logger.info('Starting transformation pipeline', {
            jobId: selectedJob.id,
            transformationCount: transformations.length,
            isD2D
          });
          response = await approveExportAndStartTransformation(
            accessToken || '',
            selectedJob.id,
            transformationConfig
          );
        } else {
          // No transformations and not D2D — call the dedicated skip endpoint which copies
          // export files to the transformed folder and advances directly to ImportQueued.
          logger.info('Skipping transformation, queueing import directly', {
            jobId: selectedJob.id
          });
          response = await skipTransformation(accessToken || '', selectedJob.id);
        }
      }

      logger.info(
        isRerun || isEditMode
          ? `${isEditMode ? 'Edit mode' : 'Rerun'} export approval succeeded for "${selectedJob.name}" — ${needsTransformationPipeline ? 'transformation queued' : 'import queued (skip)'
          }`
          : `Export approval succeeded for "${selectedJob.name}" — ${needsTransformationPipeline ? 'transformation queued' : 'import queued (skip)'
          }`,
        { jobId: selectedJob.id, isRerun, isEditMode, hasTransformations, isD2D, response }
      );

      navigate('/dashboard', {
        replace: isEditMode, // Replace history when editing to prevent going back to Approve Transformations page
        state: {
          message: needsTransformationPipeline
            ? `Transformation queued for ${selectedJob.name}`
            : `Import queued for ${selectedJob.name}`,
          updatedMigration: response
        }
      });
    } catch (err) {
      logger.error(
        `Export approval ${isRerun || isEditMode ? (isEditMode ? 'edit/rerun' : 'rerun') : 'start'} failed for "${selectedJob.name}"`,
        { error: err, jobId: selectedJob.id, isRerun, isEditMode, hasTransformations }
      );
      showToast('Export approval failed. Please try again.', 'error');
      console.error('Export approval failed', err);
    } finally {
      setStartTransformation(false);
    }
  };

  const addNewTransformation = useCallback(() => {
    // Step 1: Collapse all existing transformations first
    setTransformations((prev) => prev.map((t) => ({ ...t, isCollapsed: true })));

    // Step 2: After collapse animation completes, add the new transformation
    setTimeout(() => {
      setTransformations((prev) => {
        const newTransformation: TransformationFormData = {
          id: `transformation-${Date.now()}`,
          transformationType: '',
          resourceTypes: [],
          displayName: '',
          isCollapsed: false,
          transformation: {
            enabled: true,
            name: '',
            order: prev.length + 1,
            parameters: {}
          }
        };
        return [...prev, newTransformation];
      });
    }, 550); // Wait for collapse animation to complete smoothly
  }, []);

  const updateTransformation = useCallback(
    (id: string, updates: Partial<TransformationFormData>) => {
      setTransformations((prev) => prev.map((t) => (t.id === id ? { ...t, ...updates } : t)));
    },
    []
  );

  const removeTransformation = useCallback((id: string) => {
    setTransformations((prev) => {
      const filtered = prev.filter((t) => t.id !== id);
      // Reassign orders to maintain sequential numbering
      return filtered.map((t, index) => ({
        ...t,
        transformation: {
          ...t.transformation,
          order: index + 1
        }
      }));
    });
  }, []);

  const toggleCollapseTransformation = useCallback((id: string) => {
    setTransformations((prev) =>
      prev.map((t) => (t.id === id ? { ...t, isCollapsed: !t.isCollapsed } : t))
    );
  }, []);

  const leftContent = (
    <>
      {isRerun && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg px-4 py-3 flex items-start gap-3">
          <svg
            className={`${ui.svg.sm} text-yellow-600 dark:text-yellow-400 mt-0.5 shrink-0`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
            />
          </svg>
          <div>
            <p className={`${ui.text.label} font-semibold text-yellow-800 dark:text-yellow-300`}>
              Redoing export approval
            </p>
            <p className={`${ui.text.xs} text-yellow-700 dark:text-yellow-400 mt-0.5`}>
              This migration previously failed at the{' '}
              <span className="font-medium">Import</span> stage. Adjust
              transformations if needed and click <span className="font-medium">Redo</span> to
              re-submit using the same migration — no new migration will be created.
            </p>
          </div>
        </div>
      )}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg px-4 py-3">
        <p className={`${ui.text.label} font-semibold text-gray-700 dark:text-gray-300`}>
          The export for{' '}
          <span className="font-semibold text-green-600 dark:text-green-500">
            {selectedJob?.name}
          </span>{' '}
          has completed successfully. Next, review the results—then optionally create transformations before applying to the target organization{' '}
          <span className="font-semibold text-green-600 dark:text-green-500">
            {selectedJob?.targetOrgName ? (
              <>
                {(() => {
                  const org = organizations.find(o => o.name === selectedJob.targetOrgName || o.shortName === selectedJob.targetOrgName);
                  return `${org?.name ?? selectedJob.targetOrgName} - ${formatRegion(org?.region)}`;
                })()}
              </>
            ) : ''}
          </span>
          {' '}If no transformations are required after review, select Next to continue to the import step.
        </p>
      </div>
      {isEditMode && !isRerun && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg px-4 py-3 flex items-start gap-3">
          <svg
            className={`${ui.svg.sm} text-yellow-600 dark:text-yellow-400 mt-0.5 shrink-0`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
          <div>
            <p className={`${ui.text.label} font-semibold text-yellow-800 dark:text-yellow-300`}>
              Editing transformations
            </p>
            <p className={`${ui.text.xs} text-yellow-700 dark:text-yellow-400 mt-0.5`}>
              You are editing the transformations for this migration. Any changes you make and submit will <span className="font-medium">replace the existing transformations</span> and rerun the transformation process with your updated configuration.
            </p>
          </div>
        </div>
      )}
      <div className={`flex items-baseline ${ui.gap.md} ${ui.mb.title}`}>
        <h1 className={`${ui.text.pageTitle} font-bold text-gray-800 dark:text-gray-100`}>
          Create Transformation(s)
        </h1>
      </div>
      <p className={`${ui.text.label} text-gray-700 dark:text-gray-300 text-left ${ui.mb.title}`}>
        <strong>Now is the time to add any transformations. </strong>
        Specify how exported values should be changed before importing into the target organization.
        Items are handled sequentially in the order they are entered.
        Add as many transformation pairs as needed below.
      </p>

      {/* D2D auto-applied Division Mapping info card */}
      {isD2D && (() => {
        const divisionMappingTransform = selectedJob?.transformationConfig?.transformations?.find(
          (t) => t.name?.toLowerCase() === 'divisionmapping'
        );
        const divisionMap = divisionMappingTransform?.parameters?.['divisionMap'] as Record<string, string> | undefined;
        const [sourceId, targetId] = divisionMap ? Object.entries(divisionMap)[0] ?? [] : [];
        const sourceName = selectedJob?.sourceDivisionName
          ?? d2dDivisions.find((d) => d.id === sourceId)?.name
          ?? sourceId;
        const targetName = selectedJob?.targetDivisionName
          ?? d2dDivisions.find((d) => d.id === targetId)?.name
          ?? targetId;
        return (
          <div className={`flex items-start ${ui.gap.md} ${ui.page.sectionPadding} bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg ${ui.mb.title}`}>
            <svg className={`${ui.svg.sm} text-blue-600 dark:text-blue-400 shrink-0 mt-0.5`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className={`${ui.text.label} font-semibold text-blue-800 dark:text-blue-300`}>
                Division Mapping — auto-applied
              </p>
              <p className={`${ui.text.xs} text-blue-700 dark:text-blue-400 mt-0.5`}>
                This is a Division-to-Division migration. A Division Mapping transformation has been automatically applied and cannot be removed.
              </p>
              {d2dDivisionsLoading ? (
                <div className={`flex items-center ${ui.gap.xs} mt-2`}>
                  <svg className={`animate-spin ${ui.svg.sm} text-blue-500 dark:text-blue-400`} viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  <span className={`${ui.text.xs} text-blue-600 dark:text-blue-400`}>Loading division names…</span>
                </div>
              ) : sourceName && targetName && d2dDivisions.length > 0 && (
                <table className={`mt-2 border-collapse ${ui.text.xs} text-blue-700 dark:text-blue-400`}>
                  <tbody>
                    <tr>
                      <td className="pr-4 py-0.5 font-medium whitespace-nowrap">Source division</td>
                      <td className="py-0.5">{sourceName}</td>
                    </tr>
                    <tr>
                      <td className="pr-4 py-0.5 font-medium whitespace-nowrap">Target division</td>
                      <td className="py-0.5">{targetName}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          </div>
        );
      })()}

      {/* Pre-export / validation warnings — surfaces pre-export validation reasons and the
          [DRAFT-FLOW] warnings the Exporter records in job1Export.warnings when a requested
          flow has no published version and cannot be exported. Kept in its own banner (above the
          transform/import warnings) so the user acts on export-phase issues before approving. */}
      <MigrationWarningsBanner
        warnings={selectedJob?.job1Export?.warnings ?? []}
        title={
          selectedJob?.status === 'ValidationFailed'
            ? 'Validation warnings — resolve these before continuing'
            : 'Pre-export validation warnings'
        }
      />

      {/* Transform/import warnings — surfaces skipped division_id remaps ([DIV-MAP]) and
          resource-check division conflicts so the user is never silently left with a
          "success" state when division assignment was not changed. */}
      <MigrationWarningsBanner
        warnings={[
          ...(selectedJob?.job2Transform?.warnings ?? []),
          ...(selectedJob?.job3Import?.warnings ?? []),
        ]}
        title="Transformation warnings"
      />

      {/* Transformations List */}
      <motion.div layout className={`flex flex-col ${ui.gap.xl}`}>
        <AnimatePresence initial={false} mode="popLayout">
          {transformations.map((transformation, index) => (
            <TransformationForm
              key={transformation.id}
              transformation={transformation}
              index={index}
              sourceOrgName={selectedJob?.sourceOrgName || ''}
              targetOrgName={selectedJob?.targetOrgName || ''}
              availableResourceTypes={selectedResourceTypes}
              resourcesByType={resourcesByType}
              transformationTypes={availableTransformationTypes}
              isD2D={isD2D}
              sourceDivisionName={selectedJob?.sourceDivisionName ?? ''}
              targetDivisionName={selectedJob?.targetDivisionName ?? ''}
              onUpdate={updateTransformation}
              onRemove={removeTransformation}
              onToggleCollapse={toggleCollapseTransformation}
            />
          ))}
        </AnimatePresence>

        {/* Add Transformation Button */}
        <motion.button
          type="button"
          onClick={addNewTransformation}
          className={`w-full ${ui.page.blockPy} border-2 border-dashed border-gray-400 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-blue-500 dark:hover:border-blue-400 transition-all group`}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
        >
          <div className="flex items-center justify-center gap-2">
            <svg
              className={`${ui.svg.lg} text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            <span className={`${ui.text.subTitle} font-semibold text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors`}>
              Add Transformation
            </span>
          </div>
        </motion.button>
      </motion.div>
    </>
  );

  const rightHeader = (
    <>
      <SelectedResourcesTableHeader
        typeSearch={typeSearch}
        onSetTypeSearchChange={setTypeSearch}
        nameSearch={nameSearch}
        onSetNameSearchChange={setNameSearch}
      />
    </>
  );

  const rightBody = (
    <>
      <SelectedResourcesTableBody
        filteredResourcesByType={filteredResourcesByType}
        filteredResourceTypes={filteredResourceTypes}
        typeSearch={typeSearch}
        nameSearch={nameSearch}
        isFullSnapshotMode={isFullSnapshotMode}
        loadingResources={exportedResourcesLoading}
        exportedResourcesError={exportedResourcesError}
        emptyMessage="No exported resources were found for this migration."
      />
    </>
  );

  const handleStartTransformationWithValidation = () => {
    setHasAttemptedSubmit(true);
    if (validateForm()) {
      setConfirmApproveOpen(true);
    }
  };

  const buttons = (
    <div className="flex flex-col items-end gap-2 w-full">
      {hasAttemptedSubmit && validationError.length > 0 && (
        <div className={`${ui.input.base} bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg max-w-sm w-full`}>
          <ul className="list-disc pl-5 space-y-1">
            {validationError.map((err, idx) => (
              <li key={idx} className={`${ui.text.label} text-red-700 dark:text-red-300`}>
                {err}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className={`flex ${ui.gap.lg} justify-end`}>
        <div className="relative group/viewlogs">
          <button
            className={`rounded-lg ${ui.input.base} bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white font-semibold shadow transition`}
            onClick={() => navigate('/MigrationLogs', {
              state: {
                job: selectedJob
              }
            })}
          >
            View Logs
          </button>
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/viewlogs:block z-50 pointer-events-none w-56">
            <div className={`bg-gray-900 dark:bg-gray-800 text-white ${ui.text.sm} font-medium px-2.5 py-1.5 rounded-lg shadow-lg leading-snug text-center`}>
              View the detailed activity logs for this migration.
            </div>
            <div className="w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45 mx-auto -mt-1" />
          </div>
        </div>
        <div className="relative group/download">
          <button
            className={`rounded-lg ${ui.input.base} bg-purple-600 dark:bg-purple-700 text-white font-semibold shadow-sm transition hover:bg-purple-700 dark:hover:bg-purple-800 flex items-center justify-center gap-2 ${downloadLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
            onClick={async () => {
              if (!accessToken || !selectedJob) return;
              setDownloadLoading(true);
              try {
                await downloadMigrationResults(accessToken, selectedJob.id);
                logger.info('Download completed', { jobId: selectedJob.id });
                showToast('Download started successfully', 'success');
              } catch (error) {
                if (error instanceof NoResultsAvailableError) {
                  showToast('No results are available for this migration', 'warning');
                } else {
                  showToast('Failed to download migration results', 'error');
                }
                logger.error('Download failed', { error });
              } finally {
                setDownloadLoading(false);
              }
            }}
            disabled={downloadLoading}
          >
            {downloadLoading && (
              <svg className={`animate-spin ${ui.svg.sm} text-white`} viewBox="0 0 24 24" aria-hidden="true">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
            )}
            <span>{downloadLoading ? 'Downloading...' : 'Download (Optional)'}</span>
          </button>
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/download:block z-50 pointer-events-none w-56">
            <div className={`bg-gray-900 dark:bg-gray-800 text-white ${ui.text.sm} font-medium px-2.5 py-1.5 rounded-lg shadow-lg leading-snug text-center`}>
              Optional: download exported results for offline review. You can continue with Next
              without downloading.
            </div>
            <div className="w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45 mx-auto -mt-1" />
          </div>
        </div>
        {(isEditMode || isRerun) && (
          <div className="relative group/back">
            <button
              className={`rounded-lg ${ui.button.wide} bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-100 font-semibold shadow transition`}
              onClick={() => {
                if (isEditMode) {
                  navigate('/ApproveTransformationsPage', { state: { job: selectedJob } });
                } else {
                  // Rerun mode - go back to wherever the user launched this from
                  smartBack();
                }
              }}
            >
              Back
            </button>
            <div className="absolute bottom-full right-0 mb-2 hidden group-hover/back:block z-50 pointer-events-none w-56">
              <div className={`bg-gray-900 dark:bg-gray-800 text-white ${ui.text.sm} font-medium px-2.5 py-1.5 rounded-lg shadow-lg leading-snug text-center`}>
                {isEditMode
                  ? 'Return to the Approve Transformations page to review your already existing transformations.'
                  : 'Return to the migration details popup screen.'}
              </div>
              <div className="w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45 ml-auto mr-3 -mt-1" />
            </div>
          </div>
        )}
        <div className="relative group/starttransform">
          <button
            className={`rounded-lg ${ui.button.wide} dark:text-gray-100 text-white font-semibold shadow transition
            ${!isButtonEnabled
                ? 'bg-gray-300 dark:bg-gray-600 opacity-60 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600'
              }`}
            onClick={handleStartTransformationWithValidation}
          >
            Next
          </button>
          <div className="absolute bottom-full right-0 mb-2 hidden group-hover/starttransform:block z-50 pointer-events-none w-56">
            <div className={`bg-gray-900 dark:bg-gray-800 text-white ${ui.text.sm} font-medium px-2.5 py-1.5 rounded-lg shadow-lg leading-snug text-center`}>
              Review your export details before approving.
            </div>
            <div className="w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45 ml-auto mr-3 -mt-1" />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {isRerun && exportedResourcesLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="flex flex-col items-center gap-4 rounded-xl bg-white px-12 py-10 shadow-2xl dark:bg-[#0f1720] min-w-[320px]">
            {/* 12-spoke segmented activity-indicator spinner */}
            <svg
              viewBox="0 0 48 48"
              className="w-16 h-16"
              aria-hidden="true"
            >
              {Array.from({ length: 12 }).map((_, i) => (
                <rect
                  key={i}
                  x="22"
                  y="4"
                  width="4"
                  height="10"
                  rx="2"
                  fill="currentColor"
                  className="text-blue-500"
                  opacity={((i + 1) / 12).toFixed(2)}
                  transform={`rotate(${i * 30} 24 24)`}
                  style={{
                    animation: 'prefill-spoke-fade 1s linear infinite',
                    animationDelay: `${-(12 - i) * (1 / 12)}s`
                  }}
                />
              ))}
            </svg>
            <div className="flex flex-col items-center gap-1 text-center">
              <p className={`${ui.text.subTitle} font-semibold text-gray-900 dark:text-gray-100`}>
                Loading your export results
              </p>
              <p className={`${ui.text.body} text-gray-500 dark:text-gray-400`}>
                Fetching the exported resources from the failed migration so you can review and adjust before resubmitting.
              </p>
            </div>
            <ul className={`${ui.text.xs} w-full space-y-1 text-gray-500 dark:text-gray-400`}>
              <li className="flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                Retrieving previously exported resources
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                Restoring any existing transformations
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                Preparing the approval page — please wait
              </li>
            </ul>
          </div>
        </div>
      )}
      <SetupLayout
        title={selectedJob?.name || 'Approve Export'}
        topBanner={
          !selectedJob?.isSnapshot ? (
            <MigrationProgressStepper steps={APPROVE_EXPORT_STEPS} />
          ) : undefined
        }
        leftContent={leftContent}
        rightHeader={rightHeader}
        rightBody={rightBody}
        rightContentWrapperClass={tableScrollClass}
        selectedResourceTitle={selectedResourceTitle}
        selectedResourceCount={totalSelectedCount}
        buttonSection={buttons}
      ></SetupLayout>
      <ConfirmApproveExportModal
        isOpen={confirmApproveOpen}
        onClose={() => setConfirmApproveOpen(false)}
        onConfirm={() => {
          // D2D migrations always run the transformation pipeline (DivisionMapping is auto-applied),
          // so skip PreMigrationConfirmModal (which exists only for the skip-to-import path).
          // For non-D2D: show PreMigrationConfirmModal only when the user added no transformations.
          if (transformations.length === 0 && !isD2D) {
            setConfirmApproveOpen(false);
            setPreMigrationConfirmOpen(true);
          } else {
            // Transformations present (or D2D) — keep the modal open to show loading state
            void handleStartTransformation();
          }
        }}
        onNoTransformConfirm={() => {
          setConfirmApproveOpen(false);
          // This is called when confirming with no transformations
          setPreMigrationConfirmOpen(true);
        }}
        job={selectedJob}
        resources={resources}
        transformations={transformations}
        transformationTypes={availableTransformationTypes}
        isRerun={isRerun}
        isEditMode={isEditMode}
        organizations={organizations}
        confirmLoading={startTransformation}
        isD2D={isD2D}
        {...(() => {
          const dm = selectedJob?.transformationConfig?.transformations
            ?.find((t) => t.name?.toLowerCase() === 'divisionmapping')
            ?.parameters?.['divisionMap'] as Record<string, string> | undefined;
          const sourceId = dm ? Object.keys(dm)[0] : undefined;
          const targetId = dm ? Object.values(dm)[0] : undefined;
          const srcName = d2dDivisions.find((d) => d.id === sourceId)?.name;
          const tgtName = d2dDivisions.find((d) => d.id === targetId)?.name;
          return {
            ...(srcName !== undefined && { d2dSourceDivisionName: srcName }),
            ...(tgtName !== undefined && { d2dTargetDivisionName: tgtName })
          };
        })()}
      />
      <PreMigrationConfirmModal
        isOpen={preMigrationConfirmOpen}
        onClose={() => setPreMigrationConfirmOpen(false)}
        onBack={() => {
          setPreMigrationConfirmOpen(false);
          setConfirmApproveOpen(true);
        }}
        onConfirm={() => {
          void handleStartTransformation();
        }}
        confirmLoading={startTransformation}
      />
    </>
  );
};

export default ApproveExportPage;
